/**
 * StreetView - Google Street View Implementation
 * RoofMapper v3 - NIKO Energy
 */

import React, { useEffect, useRef, useState } from 'react';
import { useMap } from '../../contexts/MapContext';

interface StreetViewProps {
  onViewReady?: (panorama: google.maps.StreetViewPanorama) => void;
}

export default function StreetView({ onViewReady }: StreetViewProps): JSX.Element | null {
  const { pinPosition, cameraState } = useMap();
  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [streetViewAvailable, setStreetViewAvailable] = useState(true);

  // Initialize Street View
  useEffect(() => {
    const initStreetView = async () => {
      try {
        if (!window.google?.maps) {
          console.error('Google Maps API not loaded');
          return;
        }

        if (!containerRef.current) {
          console.error('Container ref not ready');
          return;
        }

        const position = pinPosition || cameraState.center || {
          lat: 19.42241280654638,
          lng: -99.21513116670044,
        };

        // Create Street View Panorama
        const panorama = new google.maps.StreetViewPanorama(containerRef.current, {
          position: { lat: position.lat, lng: position.lng },
          pov: {
            heading: cameraState.heading || 0,
            pitch: 0,
          },
          zoom: 1,
          addressControl: true,
          linksControl: true,
          panControl: true,
          enableCloseButton: false,
          fullscreenControl: false,
        });

        panoramaRef.current = panorama;

        // Check if Street View is available at this location
        const streetViewService = new google.maps.StreetViewService();
        streetViewService.getPanorama(
          {
            location: { lat: position.lat, lng: position.lng },
            radius: 50,
          },
          (data, status) => {
            if (status === google.maps.StreetViewStatus.OK && data) {
              setStreetViewAvailable(true);
            } else {
              setStreetViewAvailable(false);
            }
          }
        );

        // Listen for POV changes
        panorama.addListener('pov_changed', () => {
          const pov = panorama.getPov();
        });

        // Listen for position changes
        panorama.addListener('position_changed', () => {
          const position = panorama.getPosition();
          if (position) {
          }
        });

        setIsReady(true);
        if (onViewReady) onViewReady(panorama);

      } catch (error) {
        console.error('Error initializing Street View:', error);
      }
    };

    const timeout = setTimeout(initStreetView, 100);
    return () => clearTimeout(timeout);
  }, []);

  // Update Street View position when pin position changes
  useEffect(() => {
    if (!panoramaRef.current || !pinPosition) return;

    const streetViewService = new google.maps.StreetViewService();
    streetViewService.getPanorama(
      {
        location: { lat: pinPosition.lat, lng: pinPosition.lng },
        radius: 50,
      },
      (data, status) => {
        if (status === google.maps.StreetViewStatus.OK && data && data.location) {
          panoramaRef.current!.setPosition(data.location.latLng!);
          setStreetViewAvailable(true);
        } else {
          setStreetViewAvailable(false);
        }
      }
    );
  }, [pinPosition]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="absolute inset-0" />
      
      {!streetViewAvailable && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 z-10">
          <div className="bg-white rounded-lg p-6 shadow-lg text-center max-w-sm">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              Street View Not Available
            </p>
            <p className="text-sm text-gray-600">
              Google Street View imagery is not available at this location. Try a different area.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

